import { useAxios, useHealthblocksSWR, useSubjectId } from '@healthblocks-io/core'
import { isToday, startOfDay } from 'date-fns/esm'
import { omit } from 'ramda'
import { useCallback } from 'react'

import { useAxiosAsync } from '@/@bothrs-react/hooks/useAxiosAsync'
import { sortByDate } from '@/modules/shared/helpers/sort-by-date'

import type {
  ActivityStatus,
  ActivityType,
  Careplan,
  CareplanActivity,
  CareplanActivityMeta,
} from '@healthblocks-io/core'
import type { PartiallyRequired } from '@/utils/typescript'

/**
 * sender?: 'patient' | 'hospital'
    category?: 'Medische vraag'
    message?: string
    doctorId?: number
 */

type CreateCareplanActivityParameters<Meta = { questionnaireSlug?: string; profileHealthRequest?: boolean }> = {
  type: ActivityType
  status: 'scheduled' | 'cancelled'
  display: string
  actorRole: 'subject' | 'doctor'
  plannedAt: string
  meta: Meta
  endAt?: string
  processAt?: string
  startedAt?: string
  questionnaireId?: number
  careplanId?: number
  appointmentId?: number
}

type CareplanActivityWithCareplan = {
  careplan: Careplan
  trigger?: string
  external?: number
  continuous?: boolean
} & CareplanActivity<ActivityType, CareplanActivityMeta>

type CareplanActivityGetParameters = {
  actorRole?: string[]
  careplanId?: number[]
  memberId?: number
  include?: ('appointment' | 'careplan' | 'children' | 'questionnaire' | 'questionnaireResponse.answers' | 'subject')[]
  sort?: 'ASC' | 'DESC'
  trigger?: string
  status?: ActivityStatus
}

export function useBrochuresActivities() {
  const subjectId = useSubjectId()
  return useHealthblocksSWR<CareplanActivity[]>(
    subjectId
      ? '/careplan-activity?' +
          new URLSearchParams({
            type: 'Message',
            subjectId: subjectId.toString(),
            actorRole: 'subject',
          })
      : null,
  )
}

function useCareplanActivity(
  types: string[] = [
    'Message',
    'PatientCommunication',
    'Questionnaire',
    'Remecare',
    'Hylyght',
    'Content',
    'FileUploadRequest',
  ],
) {
  const axios = useAxios()
  const memberId = useSubjectId()

  // Methods
  // ------------------------------------------------------------------------- /
  const getCareplanActivities = useCallback(
    (parameters?: CareplanActivityGetParameters) => {
      return axios
        .get<CareplanActivityWithCareplan[]>('careplan-activity', {
          params: {
            subjectId: memberId,
            include: ['questionnaire', 'careplan'].join(','),
            type: types.join(','),
            actorRole: ['subject'].join(','),
            ...parameters,
          },
        })
        .then((response) => {
          return {
            ...response,
            data: [...response.data].sort(
              sortByDate<CareplanActivityWithCareplan>(['plannedAt'], parameters?.sort || 'ASC'),
            ),
          }
        })
    },
    [axios, memberId, types],
  )

  const getCareplanActivityById = useCallback(
    (id: number) => {
      return axios.get<CareplanActivityWithCareplan>(`/careplan-activity/${id}`, {
        params: {
          subjectId: memberId,
          include: [
            'questionnaire',
            'careplan',
            'questionnaireResponse.answers',
            'questionnaireResponse.answers.files',
          ].join(','),
          type: types.join(','),
          actorRole: ['subject'].join(','),
        },
      })
    },
    [axios, memberId, types],
  )

  const getCareplanActivitiesForToday = useCallback(() => {
    return getCareplanActivities().then((value) => {
      if (!value.data) {
        return value
      }

      // Filter activities based on schedule constraints
      const filteredData = {
        ...value,
        data: value.data.filter((activity) => {
          const startDate = startOfDay(new Date(activity.plannedAt))
          const isScheduled = activity.status === 'scheduled'
          return isToday(startDate) && isScheduled
        }),
      }

      // Remove duplicate (display, questionnaireId) pairs while keeping the first occurrence
      const seen = new Set()
      const filteredDataWithQuestionnaireId = filteredData.data.filter((activity) => {
        if (!activity.questionnaireId || !activity.continuous) {
          return true
        }

        const key = `${activity.display}-${activity.questionnaireId}`
        if (seen.has(key)) {
          return false
        }

        seen.add(key)
        return true
      })

      return { ...filteredData, data: filteredDataWithQuestionnaireId }
    })
  }, [getCareplanActivities])

  // This call is meant for mocking purpose only.
  // All create careplan activities should come from Synops or HB
  const createCareplanActivity = useCallback(
    <Meta>(parameters: CreateCareplanActivityParameters<Meta>) => {
      return axios.post<CareplanActivityWithCareplan>('/careplan-activity', {
        ...parameters,
        subjectId: memberId,
      })
    },
    [axios, memberId],
  )

  const updateCareplanActivity = useCallback(
    (payload: PartiallyRequired<Partial<CareplanActivity>, 'id'>) => {
      return axios.patch(`/careplan-activity/${payload.id}`, omit(['id'], payload))
    },
    [axios],
  )

  return {
    getCareplanActivities: useAxiosAsync(getCareplanActivities, true),
    getCareplanActivityById: useAxiosAsync(getCareplanActivityById, true),
    getCareplanActivitiesForToday: useAxiosAsync(getCareplanActivitiesForToday, true),
    createCareplanActivity: useAxiosAsync(createCareplanActivity),
    updateCareplanActivity: useAxiosAsync(updateCareplanActivity),

    careplanActivitiesFetcher: getCareplanActivities,
    careplanActivityByIdFetcher: getCareplanActivityById,
    careplanActivitiesForTodayFetcher: getCareplanActivitiesForToday,
  }
}

export type { CareplanActivityWithCareplan }
export { useCareplanActivity }
